import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";
import {
    FeedAudienceSelect,
    FeedControlLabel,
    FeedColumn,
    FeedHeaderIntro,
    FeedLoadSentinel,
    FeedMainGrid,
    FeedPageBackground,
    FeedPageDescription,
    FeedPageTitle,
    FeedRetryButton,
    FeedSidebarColumn,
    FeedShell,
    FeedSkeletonBlock,
    FeedSkeletonRow,
    FeedSkeletonSurface,
    FeedSkeletonTextStack,
    FeedEndState,
    FeedStateContent,
    FeedStateSurface,
    FeedStateText,
    FeedStateTitle,
    NoticeboardViewCard,
    NoticeboardViewCardButton,
    NoticeboardViewCardText,
    NoticeboardViewCardTitle
} from "src/components/NoticeboardFeedUi";
import { CLIENT_NOTICEBOARD, NOTICEBOARD } from "src/constants/permissions";
import { listFeedNotices } from "../../api/feedApi";
import { mergeNoticeFeedUpdate } from "../../feedUtils";
import NoticeboardFeedCard from "./NoticeboardFeedCard";
import NoticeboardPinnedPosts from "./NoticeboardPinnedPosts";

const FEED_BATCH_SIZE = 5;
const FEED_AUDIENCE_SESSION_KEY = "noticeboardFeedAudience";

const FeedSkeleton = () => (
    <FeedSkeletonSurface aria-hidden="true">
        <FeedSkeletonRow>
            <FeedSkeletonBlock $width="2.75rem" $height="2.75rem" $radius="50%" />
            <FeedSkeletonTextStack>
                <FeedSkeletonBlock $width="42%" $height="0.8rem" />
                <FeedSkeletonBlock $width="28%" $height="0.65rem" />
            </FeedSkeletonTextStack>
        </FeedSkeletonRow>
        <FeedSkeletonBlock $width="68%" $height="1.2rem" />
        <FeedSkeletonBlock $height="0.8rem" />
        <FeedSkeletonBlock $width="88%" $height="0.8rem" />
        <FeedSkeletonBlock $height="18rem" $radius="10px" />
    </FeedSkeletonSurface>
);

const NoticeboardFeedView = ({
    user,
    canManage,
    canCreateClientPost,
    canUseCheckedIn,
    onCreateClientPost,
    onOpenManagement,
    refreshKey
}) => {
    const canManageEmployees = user.permissions.includes(NOTICEBOARD);
    const canManageClients = user.permissions.includes(CLIENT_NOTICEBOARD);
    const employeeName = capitalizeString(pluralize.singular(customNames.employees));
    const clientName = capitalizeString(pluralize.singular(customNames.clients));
    const availableAudiences = useMemo(() => {
        const audiences = [];
        if (canManageEmployees) {
            audiences.push({ value: "employee", label: pluralize(employeeName) });
        }
        if (canManageClients) {
            audiences.push({ value: "client", label: pluralize(clientName) });
        }
        return audiences;
    }, [canManageClients, canManageEmployees, clientName, employeeName]);
    const [audience, setAudience] = useState(() => {
        if (!canManage) {
            return null;
        }
        const savedAudience = window.sessionStorage.getItem(FEED_AUDIENCE_SESSION_KEY);
        if (availableAudiences.some(option => option.value === savedAudience)) {
            return savedAudience;
        }

        return availableAudiences[0]?.value || null;
    });
    const [notices, setNotices] = useState([]);
    const [visibleCount, setVisibleCount] = useState(FEED_BATCH_SIZE);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const sentinelRef = useRef(null);
    const requestIdRef = useRef(0);

    const loadFeed = useCallback(async () => {
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        setLoading(true);
        setError(false);
        setVisibleCount(FEED_BATCH_SIZE);
        try {
            const response = await listFeedNotices(user.token, canManage ? audience : null);
            const uniqueNotices = Array.from(
                new Map(response.map(notice => [notice.uuid, notice])).values()
            );
            const sortedNotices = [...uniqueNotices].sort((first, second) => {
                const firstDate = new Date(first.postedDate || first.created_at);
                const secondDate = new Date(second.postedDate || second.created_at);
                return secondDate - firstDate;
            });
            if (requestIdRef.current === requestId) {
                setNotices(sortedNotices);
            }
        } catch {
            if (requestIdRef.current === requestId) {
                setError(true);
                setNotices([]);
            }
        } finally {
            if (requestIdRef.current === requestId) {
                setLoading(false);
            }
        }
    }, [audience, canManage, refreshKey, user.token]);

    useEffect(() => {
        if (!canManage || audience) {
            loadFeed();
        }
    }, [audience, canManage, loadFeed]);

    useEffect(() => {
        if (canManage && audience) {
            window.sessionStorage.setItem(FEED_AUDIENCE_SESSION_KEY, audience);
        }
    }, [audience, canManage]);

    useEffect(() => {
        if (canManage && !availableAudiences.some(option => option.value === audience)) {
            setAudience(availableAudiences[0]?.value || null);
        }
    }, [audience, availableAudiences, canManage]);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (
            !sentinel ||
            visibleCount >= notices.length ||
            typeof IntersectionObserver === "undefined"
        ) {
            return undefined;
        }

        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                setVisibleCount(current => Math.min(current + FEED_BATCH_SIZE, notices.length));
            }
        }, { rootMargin: "320px 0px" });
        observer.observe(sentinel);

        return () => observer.disconnect();
    }, [notices.length, visibleCount]);

    const handleNoticeUpdated = (updatedNotice) => {
        setNotices(current => current.map(notice => (
            notice.uuid === updatedNotice.uuid ?
                mergeNoticeFeedUpdate(notice, updatedNotice) :
                notice
        )));
    };

    const handleAudienceChange = (event) => {
        setLoading(true);
        setNotices([]);
        setVisibleCount(FEED_BATCH_SIZE);
        setAudience(event.target.value);
    };

    const visibleNotices = notices.slice(0, visibleCount);
    const canManageActiveNoticeboard = canManage && (
        audience === "employee" ?
            canManageEmployees :
            audience === "client" && canManageClients
    );

    return (
        <FeedPageBackground>
            <FeedMainGrid>
                <FeedSidebarColumn
                    aria-label="Noticeboard feed controls and pinned posts"
                    tabIndex={0}
                >
                    <FeedHeaderIntro>
                        <FeedPageTitle>Noticeboard Feed</FeedPageTitle>
                        <FeedPageDescription>
                            Browse the latest published updates, media, reactions, and discussions.
                        </FeedPageDescription>
                    </FeedHeaderIntro>
                    {canCreateClientPost && (
                        <NoticeboardViewCardButton
                            type="button"
                            $fullWidth
                            onClick={onCreateClientPost}
                        >
                            New Post
                        </NoticeboardViewCardButton>
                    )}
                    {canManage && (
                        <NoticeboardViewCard>
                            <div>
                                <NoticeboardViewCardTitle>Manage Noticeboard</NoticeboardViewCardTitle>
                                <NoticeboardViewCardText>
                                    Return to the existing post creation and management tools.
                                </NoticeboardViewCardText>
                            </div>
                            <label>
                                <FeedControlLabel>Viewing audience</FeedControlLabel>
                                <FeedAudienceSelect
                                    value={audience}
                                    onChange={handleAudienceChange}
                                    aria-label="Noticeboard feed audience"
                                    disabled={availableAudiences.length < 2}
                                >
                                    {availableAudiences.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FeedAudienceSelect>
                            </label>
                            <NoticeboardViewCardButton type="button" onClick={onOpenManagement}>
                                Open Management View
                            </NoticeboardViewCardButton>
                        </NoticeboardViewCard>
                    )}
                    <NoticeboardPinnedPosts
                        notices={notices}
                        loading={loading}
                        error={error}
                        onRetry={loadFeed}
                        token={user.token}
                        audience={canManage ? audience : null}
                        employeeName={employeeName}
                        clientName={clientName}
                        canManageActiveNoticeboard={canManageActiveNoticeboard}
                        canUseCheckedIn={canUseCheckedIn}
                        onNoticeUpdated={handleNoticeUpdated}
                    />
                </FeedSidebarColumn>
                <FeedColumn aria-label="Noticeboard posts" tabIndex={0}>
                    <FeedShell>
                        {loading && (
                            <div aria-label="Loading Noticeboard feed" role="status">
                                <FeedSkeleton />
                                <FeedSkeleton />
                                <FeedSkeleton />
                            </div>
                        )}

                        {!loading && error && (
                            <FeedStateSurface>
                                <FeedStateContent>
                                    <FeedStateTitle>We couldn’t load the Noticeboard feed</FeedStateTitle>
                                    <FeedStateText>
                                        Check your connection and try again. Your audience selection has been kept.
                                    </FeedStateText>
                                    <FeedRetryButton type="button" onClick={loadFeed}>Retry</FeedRetryButton>
                                </FeedStateContent>
                            </FeedStateSurface>
                        )}

                        {!loading && !error && !notices.length && (
                            <FeedStateSurface>
                                <FeedStateContent>
                                    <FeedStateTitle>No published posts yet</FeedStateTitle>
                                    <FeedStateText>
                                        There are no published posts for this audience right now.
                                    </FeedStateText>
                                    {canManage && (
                                        <FeedRetryButton type="button" onClick={onOpenManagement}>
                                            Open Management View
                                        </FeedRetryButton>
                                    )}
                                </FeedStateContent>
                            </FeedStateSurface>
                        )}

                        {!loading && !error && visibleNotices.map(notice => (
                            <NoticeboardFeedCard
                                key={notice.uuid}
                                notice={notice}
                                token={user.token}
                                audience={canManage ? audience : null}
                                employeeName={employeeName}
                                clientName={clientName}
                                canManageActiveNoticeboard={canManageActiveNoticeboard}
                                canUseCheckedIn={canUseCheckedIn}
                                onNoticeUpdated={handleNoticeUpdated}
                            />
                        ))}

                        {!loading && !error && visibleCount < notices.length && (
                            <>
                                <FeedLoadSentinel ref={sentinelRef} aria-hidden="true" />
                                <FeedRetryButton
                                    type="button"
                                    onClick={() => setVisibleCount(current => (
                                        Math.min(current + FEED_BATCH_SIZE, notices.length)
                                    ))}
                                >
                                    Load more posts
                                </FeedRetryButton>
                            </>
                        )}

                        {!loading && !error && notices.length > 0 && visibleCount >= notices.length && (
                            <FeedEndState role="status">
                                You’re all caught up.
                            </FeedEndState>
                        )}
                    </FeedShell>
                </FeedColumn>
            </FeedMainGrid>
        </FeedPageBackground>
    );
};

export default NoticeboardFeedView;
